"use client";

import { useState, useEffect } from "react";
import { startOfDayISO } from "@/lib/utils/date";
import Link from "next/link";
import { storyblokEditable } from "@storyblok/react/rsc";

import {
  MaskShape,
  type MaskShapeVariant,
} from "@/components/illustrations/MaskShape";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  fetchStoryblokList,
  fetchEventsAndTravels,
} from "@/lib/storyblok/fetchList";
import { getUrlFromLinkObject } from "@/lib/storyblok/utils";
import type { MultilinkStoryblok } from "@/component-types-sb";

type ShapeColor =
  | "sun-100"
  | "sand-50"
  | "sand-100"
  | "sky-50"
  | "sky-100"
  | "white";

const shapeColorClasses: Record<ShapeColor, string> = {
  "sun-100": "text-sun-100",
  "sand-50": "text-sand-50",
  "sand-100": "text-sand-100",
  "sky-50": "text-sky-50",
  "sky-100": "text-sky-100",
  white: "text-white",
};

type ButtonType =
  | ""
  | "none"
  | "link"
  | "next_diatalk"
  | "next_reisen"
  | "next_topictalk"
  | "next_sprechstunde"
  | "next_community";

const buttonTypeToCategory: Record<string, string> = {
  next_diatalk: "diaTalk",
  next_reisen: "reisen",
  next_topictalk: "topicTalk",
  next_sprechstunde: "sprechstunde",
  next_community: "community",
};

type ShapeGalleryItemStoryblok = {
  shape?: MaskShapeVariant;
  shape_color?: ShapeColor;
  headline?: string;
  text?: string;
  button_label?: string;
  button_type?: ButtonType;
  button_link?: MultilinkStoryblok;
  _uid: string;
  component: "shape_gallery_item";
  [k: string]: any;
};

type ShapeGalleryItemProps = {
  blok: ShapeGalleryItemStoryblok;
};

const ShapeGalleryItem = ({ blok }: ShapeGalleryItemProps) => {
  const shape = (blok.shape || "four_vertical_even") as MaskShapeVariant;
  const shapeColor = (blok.shape_color || "sun-100") as ShapeColor;
  const colorClass = shapeColorClasses[shapeColor] || "text-sun-100";

  const buttonType = blok.button_type || "none";
  const eventCategory = buttonTypeToCategory[buttonType];
  const [nextEventHref, setNextEventHref] = useState<string | null>(null);

  useEffect(() => {
    if (!eventCategory) return;

    const fetchNextEvent = async () => {
      const now = startOfDayISO();

      try {
        // Trips are their own content type without a category; the
        // info sessions about them are still events with category
        // `reiseInfo`. Both belong under "Reisen" for a visitor.
        const stories =
          eventCategory === "reisen"
            ? await fetchEventsAndTravels({
                filter_query: { date: { gt_date: now } },
                eventOnlyFilter: { category: { in: "reiseInfo" } },
                sort_by: "content.date:asc",
                per_page: 1,
                exact: true,
              })
            : await fetchStoryblokList({
                content_type: "event",
                filter_query: {
                  date: { gt_date: now },
                  category: { in: eventCategory },
                },
                sort_by: "content.date:asc",
                per_page: 1,
              });

        if (stories && stories.length > 0) {
          setNextEventHref(`/${stories[0].full_slug}`);
        }
      } catch (error) {
        console.error("Error fetching next event:", error);
      }
    };

    fetchNextEvent();
  }, [eventCategory]);

  // Resolve button href
  const buttonHref = eventCategory
    ? nextEventHref
    : getUrlFromLinkObject(blok.button_link) || null;

  const hasButton = blok.button_label && buttonHref;

  return (
    <div
      className="relative isolate flex flex-col h-full w-[280px] md:w-full overflow-hidden rounded-3xl px-6 py-8 md:px-8 md:py-10"
      {...storyblokEditable(blok)}
    >
      {/* Shape background */}
      <div className="absolute top-0 right-0 bottom-0 left-0 z-[1]">
        <MaskShape
          variant={shape}
          className={cn("w-full h-full", colorClass)}
          preserveAspectRatio="none"
        />
      </div>

      {/* Content */}
      <div className="relative z-[2] flex flex-col items-center justify-center text-center [&>*+*]:mt-4 h-full">
        <div className="w-full">
          {blok.headline && (
            <p className="text-headline-md-static">{blok.headline}</p>
          )}
          {blok.text && <p className="text-copy-snug">{blok.text}</p>}
        </div>

        {hasButton && (
          <div>
            <Link
              href={buttonHref}
              target={blok.button_link?.target || "_self"}
            >
              <Button variant="black" size="sm">
                {blok.button_label}
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShapeGalleryItem;
